/**
 * StockLevelsView Component
 *
 * Displays inventory trends data as a stacked area chart.
 * Part of the Analytics Card split for better maintainability.
 *
 * NOTE: polaris-viz is NOT SSR-compatible (uses useLayoutEffect and window).
 * The StackedAreaChart is wrapped with ClientOnly to prevent hydration errors.
 *
 * @see TECHNICAL_DEBT_ROADMAP.md Phase 2.2
 */

import { StackedAreaChart } from '@shopify/polaris-viz';
import { PackageIcon } from '@shopify/polaris-icons';
import { SkeletonBodyText, Box } from '@shopify/polaris';
import type { InventoryTrendItem } from '../../../types/analytics';
import { TabEmptyState } from '../TabEmptyState';
import { ClientOnly } from '~/components/core/ClientOnly';

/**
 * Polaris semantic colors for inventory levels
 */
const CHART_COLORS = {
  critical: '#D72C0D', // Red - critical stock levels
  warning: '#B98900',  // Orange/Yellow - warning stock levels
  healthy: '#008060',  // Green - healthy inventory
};

/**
 * Props for StockLevelsView
 */
interface StockLevelsViewProps {
  /** Inventory trends data */
  data: InventoryTrendItem[];
}

/**
 * Transform raw inventory trends data into polaris-viz stacked area format
 */
function transformInventoryTrendsData(data: InventoryTrendItem[]) {
  return [
    {
      name: 'Critical',
      data: data.map(d => ({ key: d.date, value: d.critical })),
      color: CHART_COLORS.critical,
    },
    {
      name: 'Warning',
      data: data.map(d => ({ key: d.date, value: d.warning })),
      color: CHART_COLORS.warning,
    },
    {
      name: 'Healthy',
      data: data.map(d => ({ key: d.date, value: d.healthy })),
      color: CHART_COLORS.healthy,
    },
  ];
}

/**
 * Stock Levels View
 * Displays a stacked area chart showing inventory distribution by alert severity over time
 */
export function StockLevelsView({ data }: StockLevelsViewProps) {
  const hasData = data && data.length > 0;

  if (!hasData) {
    return (
      <div className="analytics-content-enter">
        <TabEmptyState
          icon={PackageIcon}
          title="No inventory trend data available"
          description="Inventory trends show stock levels by alert severity over time"
        />
      </div>
    );
  }

  const vizData = transformInventoryTrendsData(data);

  return (
    <div className="chart-content-enter">
      <ClientOnly
        fallback={
          <Box padding="400">
            <SkeletonBodyText lines={6} />
          </Box>
        }
      >
        {() => (
          <StackedAreaChart
            data={vizData}
            theme="Light"
            showLegend={true}
            isAnimated={true}
            yAxisOptions={{
              labelFormatter: (value) => Math.round(Number(value)).toString(),
            }}
          />
        )}
      </ClientOnly>
    </div>
  );
}
